<template>
  <div>
    <v-card>
      <v-container>
      <!-- <h1>Challenge Evaluation</h1> -->
        <h2 class="mt-3 mb-1">1. Evaluation Criteria</h2>
        <p>This Challenge is primarily evaluated using the <em>interpolated mAP (mean average precision)</em> metric, which measures the overlap between the true bounding boxes and your predicted bounding boxes. In addition, the leaderboard shows a number of secondary metrics which will be used to determine smaller "bonus" awards for models that demonstrate strong performance in specific aspects, such as for small or rare objects. Keep reading below for a more detailed explanation of how these metrics are computed.</p>

        <h2 class="mt-3 mb-1">2. Interpolated mAP (mean average precision) is the primary metric for this Challenge</h2>
        <p>Starting with a set of ground truth (GT) rectangles, a set of predicted (P) rectangles, and confidence scores for each of the rectangles, first we compare the GT and P rectangles. If the Intersection-Over-Union (IOU) of a rectangle pair is above the threshold (0.5), then the detection counts as a True Positive (TP). Otherwise that detection is a False Positive (FP). Undetected Ground Truth rectangles are False Negatives (FN). Precision and recall metrics are computed using the standard definitions: </p>

        <v-layout row justify-center class="my-3">
          <v-flex xs2 class="mx-4">
            <img src="static/precision.png" width="133px">
          </v-flex>
          <v-flex xs2 class="mx-4">
            <img src="static/recall.png" width="133px">
          </v-flex>
        </v-layout>
        <!-- this should probably be centered on the equal symbol -->

        <!-- <div> -->
          <!-- precision = <em> TP / (TP + FP)</em><br> -->
          <!-- recall = <em> TP / (TP + FN)</em><br> -->
        <!-- </div> -->

        <h3 class="mt-3 mb-1">How we compute the mAP</h3>
        <p>All the TP and FP detections are sorted by confidence score, from highest to lowest. Then precision and recall are incrementally calculated by adding each TP or FP detection. Recall is calculated by dividing the number of ground truths (all unseen detections are considered FN by default). </p>

        <v-container>
          <v-layout row justify-center>
            <v-flex xs6>
              <img src="/static/incremental_PR_metric.png" width="360px">
            </v-flex>
          </v-layout>
          <div style="display: inline-block;" class="mx-3">
            <v-container>
              <p><strong>Figure 1.</strong> Illustration of the incremental calculation of precision and recall. </p>
              <p><em>Reference: Henderson P., Ferrari V. (2017) End-to-End Training of Object Class Detectors for Mean Average Precision. In: Lai SH., Lepetit V., Nishino K., Sato Y. (eds) Computer Vision – ACCV 2016. ACCV 2016. Lecture Notes in Computer Science, vol 10115. Springer, Cham. </em><a href="https://link.springer.com/chapter/10.1007%2F978-3-319-54193-8_13" target="_blank">https://link.springer.com/chapter/10.1007%2F978-3-319-54193-8_13</a></p>
            </v-container>
              <!-- </v-card> -->
          </div>
        </v-container>

        <p>The Figure above illustrates the incremental calculation of precision and recall. A point (P,R) is computed for each detection, then plotted on a graph. Each additional TP increases recall, and each additional FP decreases precision. This yields a plot of precision as a function of recall: P(r) Average precision (AP) is the area under the function: the integral of P(r) with respect to r. As you can see in the image, the graph may be jagged. We ignore the jaggedness by computing "interpolated" average precision, which includes the pink colored area in the Figure above. The result is the interpolated mean average precision (mAP), the primary metric for this Challenge.</p>

        <h3 class="mt-3 mb-1">Intuition behind the "intersection over union" metric</h3>
        <p>Object detection tasks can be framed as a specific kind of search task, so it makes sense to think of performance in terms of precision (i.e., the fraction of predictions that are relevant) versus recall (i.e., the fraction of all possible hits that were predicted). The object detection task is more complicated that a typical search because hits are not binary and so there is not a clear definition of how to define a "hit". An object can be predicted to have many different, but similar, bounding boxes. So we need a way to compare a predicted bounding box to the ground truth bounding box and then decide if the prediction is close enough to call it a "true positive". For this Challenge, we embrace the same definition used by VOC and other object detection metrics, where a predicted bounding box is considered a "true positive" when the area of overlap with the ground truth is at least 50% of the area of the box. Multiple detections of the same object are considered to be false detections; only the first detection is true. </p>

        <h3 class="mt-3 mb-1">Looking for more background info?</h3>
        <p>Refer to the <a href="http://host.robots.ox.ac.uk/pascal/VOC/voc2007/devkit_doc_07-Jun-2007.pdf" target="_blank">Pascal VOC documentation</a> and <a href="https://arxiv.org/abs/1607.03476" target="_blank">this paper by Henderson and Ferrari</a> for more detailed background on object detection tasks and the mAP metric.

        <h2 class="mt-3 mb-1">3. Secondary metrics</h2>
        <p>This Challenge includes "bonus" awards for strong performance on a few secondary metrics. This section describes the secondary metrics and how they are calculated.</p>

<!--         <h3>mAP on objects of different sizes: small, medium, large</h3>
        <p>Explanation goes here.</p>

        <h3>mAP on rare vs. common objects</h3>
        <p>Explanation goes here.</p> -->

        <h3 class="mt-3 mb-1">F1 score</h3>
        <p>The F1 score is the harmonic mean of precision and recall:</p>

        <!-- <div class="mx-3 my-3"> -->
          <!-- F1 = <em> 2 / [(1/P) + 1/R)]</em><br> -->
        <!-- </div> -->

        <v-layout row justify-center class="my-3">
          <v-flex xs2 class="mx-4">
            <img src="static/F1.png" width="133px">
          </v-flex>
        </v-layout>

        <!-- <p style="color: #f44e3f">TODO: typeset these using latex or similar. The metrics are actually pretty simple but the way we have to display in HTML/text with brackets and such obfuscates them.</p> -->

        <p>F1 is the metric used to score previous overhead imagery competitions: SpaceNet, FMoW, and COWC. The xView leaderboard displays F1 for the sake of comparison, but F1 score is not used for any rankings or awards in the xView Challenge.</p>

        <h3 class="mt-3 mb-1">Object Classes</h3>
        <!-- Small, Med, Large -->
        <v-container fluid grid-list-lg>
          <v-layout row justify-left>
            <v-flex xs4 sm5>
              <h4 class="my-3">Small (0-100 px<sup>2</sup>)</h4>
              <v-data-table
                :headers="headers"
                :items="itemsSmall"
                hide-actions
                class="elevation-1"
              >
                <template slot="items" slot-scope="props">
                  <!-- <td>{{ props.item.name }}</td> -->
                  <td class="text-xs-left">{{ props.item.class_name}}</td>
                  <td class="text-xs-left">{{ props.item.class_number}}</td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>

          <v-layout row justify-left>
            <v-flex xs4 sm5>
              <h4 class="my-3">Medium (100 - 1000 px<sup>2</sup>)</h4>
              <v-data-table
                :headers="headers"
                :items="itemsMedium"
                hide-actions
                class="elevation-1"
              >
                <template slot="items" slot-scope="props">
                  <!-- <td>{{ props.item.name }}</td> -->
                  <td class="text-xs-left">{{ props.item.class_name}}</td>
                  <td class="text-xs-left">{{ props.item.class_number}}</td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>

          <v-layout row justify-left>
            <v-flex xs4 sm5>
              <h4 class="my-3">Large (> 1000 px<sup>2</sup>)</h4>
              <v-data-table
                :headers="headers"
                :items="itemsLarge"
                hide-actions
                class="elevation-1"
              >
                <template slot="items" slot-scope="props">
                  <!-- <td>{{ props.item.name }}</td> -->
                  <td class="text-xs-left">{{ props.item.class_name}}</td>
                  <td class="text-xs-left">{{ props.item.class_number}}</td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>

        </v-container>

        <!-- Common / Rare -->
        <v-container fluid grid-list-lg>
          <v-layout row justify-left>
            <v-flex xs4 sm5>
              <h4 class="my-3">Rare (fewer than 1000 instances)</h4>
              <v-data-table
                :headers="headers"
                :items="itemsRare"
                hide-actions
                class="elevation-1"
              >
                <template slot="items" slot-scope="props">
                  <!-- <td>{{ props.item.name }}</td> -->
                  <td class="text-xs-left">{{ props.item.class_name}}</td>
                  <td class="text-xs-left">{{ props.item.class_number}}</td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>

          <v-layout row justify-left>
            <v-flex xs4 sm5>
              <h4 class="my-3">Common (1000 instances or more)</h4>
              <v-data-table
                :headers="headers"
                :items="itemsCommon"
                hide-actions
                class="elevation-1"
              >
                <template slot="items" slot-scope="props">
                  <!-- <td>{{ props.item.name }}</td> -->
                  <td class="text-xs-left">{{ props.item.class_name}}</td>
                  <td class="text-xs-left">{{ props.item.class_number}}</td>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>

        </v-container>

      </v-container>
    </v-card>
  </div>
</template>

<script>
  export default {
    data () {
      return {
        // foo: 'bar'
        headers: [
          {'text': 'Class Name', 'align': 'left', 'sortable': false, 'value': 'class_name', 'class': 'cardgray'},
          {'text': 'Class ID', 'align': 'left', 'sortable': false, 'value': 'class_number', 'class': 'cardgray'}
        ],
        itemsSmall: [
          {'value': false, 'align': 'left', 'class_name': 'Passenger Vehicle', 'class_number': 17},
          {'value': false, 'class_name': 'Small car', 'class_number': 18},
          {'value': false, 'class_name': 'Bus', 'class_number': 19},
          {'value': false, 'class_name': 'Pickup Truck', 'class_number': 20},
          {'value': false, 'class_name': 'Utility Truck', 'class_number': 21},
          {'value': false, 'class_name': 'Truck', 'class_number': 23},
          {'value': false, 'class_name': 'Cargo Truck', 'class_number': 24},
          {'value': false, 'class_name': 'Truck Tractor', 'class_number': 26},
          {'value': false, 'class_name': 'Trailer', 'class_number': 27},
          {'value': false, 'class_name': 'Truck Tractor w/ Flatbed Trailer', 'class_number': 28},
          {'value': false, 'class_name': 'Crane Truck', 'class_number': 32},
          {'value': false, 'class_name': 'Motorboat', 'class_number': 41},
          {'value': false, 'class_name': 'Dump Truck', 'class_number': 60},
          {'value': false, 'class_name': 'Tractor', 'class_number': 62},
          {'value': false, 'class_name': 'Front Loader/Bulldozer', 'class_number': 63},
          {'value': false, 'class_name': 'Excavator', 'class_number': 64},
          {'value': false, 'class_name': 'Cement Mixer', 'class_number': 65},
          {'value': false, 'class_name': 'Ground Grader', 'class_number': 66},
          {'value': false, 'class_name': 'Shipping Container', 'class_number': 91}
        ],
        // headersMedium: [
        //   {'text': 'Class Name', 'align': 'left', 'sortable': false, 'value': 'class_name'},
        //   {'text': 'Class ID', 'align': 'left', 'sortable': false, 'value': 'class_number'}
        // ],
        itemsMedium: [
          {'value': false, 'class_name': 'Fixed-wing aircraft', 'class_number': 11},
          {'value': false, 'class_name': 'Small aircraft', 'class_number': 12},
          {'value': false, 'class_name': 'Helicopter', 'class_number': 15},
          {'value': false, 'class_name': 'Truck Tractor w/ Box Trailer', 'class_number': 25},
          {'value': false, 'class_name': 'Truck Tractor w/ Liquid Tank', 'class_number': 29},
          {'value': false, 'class_name': 'Railway Vehicle', 'class_number': 33},
          {'value': false, 'class_name': 'Passenger Car', 'class_number': 34},
          {'value': false, 'class_name': 'Cargo/Container Car', 'class_number': 35},
          {'value': false, 'class_name': 'Flat Car', 'class_number': 36},
          {'value': false, 'class_name': 'Tank Car', 'class_number': 37},
          {'value': false, 'class_name': 'Locomotive', 'class_number': 38},
          {'value': false, 'class_name': 'Sailboat', 'class_number': 42},
          {'value': false, 'class_name': 'Tugboat', 'class_number': 44},
          {'value': false, 'class_name': 'Fishing Vessel', 'class_number': 47},
          {'value': false, 'class_name': 'Yacht', 'class_number': 50},
          {'value': false, 'class_name': 'Engineering Vehicle', 'class_number': 53},
          {'value': false, 'class_name': 'Reach Stacker', 'class_number': 56},
          {'value': false, 'class_name': 'Mobile Crane', 'class_number': 59},
          {'value': false, 'class_name': 'Haul Truck', 'class_number': 61},
          {'value': false, 'class_name': 'Hut/Tent', 'class_number': 71},
          {'value': false, 'class_name': 'Shed', 'class_number': 72},
          {'value': false, 'class_name': 'Building', 'class_number': 73},
          {'value': false, 'class_name': 'Damaged/Demolished Building', 'class_number': 76},
          {'value': false, 'class_name': 'Helipad', 'class_number': 84},
          {'value': false, 'class_name': 'Storage Tank', 'class_number': 85},
          {'value': false, 'class_name': 'Pylon', 'class_number': 93},
          {'value': false, 'class_name': 'Tower', 'class_number': 94}
        ],
        // headersLarge: [
        //   {'text': 'Class Name', 'align': 'left', 'sortable': false, 'value': 'class_name'},
        //   {'text': 'Class ID', 'align': 'left', 'sortable': false, 'value': 'class_number'}
        // ],
        itemsLarge: [
          {'value': false, 'class_name': 'Passenger/Cargo Plane', 'class_number': 13},
          {'value': false, 'class_name': 'Maritime Vessel', 'class_number': 40},
          {'value': false, 'class_name': 'Barge', 'class_number': 45},
          {'value': false, 'class_name': 'Ferry', 'class_number': 49},
          {'value': false, 'class_name': 'Container Ship', 'class_number': 51},
          {'value': false, 'class_name': 'Oil Tanker', 'class_number': 52},
          {'value': false, 'class_name': 'Tower Crane', 'class_number': 54},
          {'value': false, 'class_name': 'Container Crane', 'class_number': 55},
          {'value': false, 'class_name': 'Straddle Carrier', 'class_number': 57},
          {'value': false, 'class_name': 'Aircraft Hangar', 'class_number': 74},
          {'value': false, 'class_name': 'Facility', 'class_number': 77},
          {'value': false, 'class_name': 'Construction Site', 'class_number': 79},
          {'value': false, 'class_name': 'Vehicle Lot', 'class_number': 83},
          {'value': false, 'class_name': 'Shipping Container Lot', 'class_number': 89}
        ],
        // headersRare: [
        //   {'text': 'Class Name', 'align': 'left', 'sortable': false, 'value': 'class_name'},
        //   {'text': 'Class ID', 'align': 'left', 'sortable': false, 'value': 'class_number'}
        // ],
        itemsRare: [
          {'value': false, 'class_name': 'Fixed-Wing Aircraft', 'class_number': 11},
          {'value': false, 'class_name': 'Small Aircraft', 'class_number': 12},
          {'value': false, 'class_name': 'Helicopter', 'class_number': 15},
          {'value': false, 'class_name': 'Truck Tractor w/ Liquid Tank', 'class_number': 29},
          {'value': false, 'class_name': 'Crane Truck', 'class_number': 32},
          {'value': false, 'class_name': 'Railway vehicle', 'class_number': 33},
          {'value': false, 'class_name': 'Flat Car', 'class_number': 36},
          {'value': false, 'class_name': 'Tank Car', 'class_number': 37},
          {'value': false, 'class_name': 'Locomotive', 'class_number': 38},
          {'value': false, 'class_name': 'Maritime Vessel', 'class_number': 40},
          {'value': false, 'class_name': 'Sailboat', 'class_number': 42},
          {'value': false, 'class_name': 'Tugboat', 'class_number': 44},
          {'value': false, 'class_name': 'Barge', 'class_number': 45},
          {'value': false, 'class_name': 'Ferry', 'class_number': 49},
          {'value': false, 'class_name': 'Yacht', 'class_number': 50},
          {'value': false, 'class_name': 'Container Ship', 'class_number': 51},
          {'value': false, 'class_name': 'Oil Tanker', 'class_number': 52},
          {'value': false, 'class_name': 'Engineering Vehicle', 'class_number': 53},
          {'value': false, 'class_name': 'Tower Crane', 'class_number': 54},
          {'value': false, 'class_name': 'Container Crane', 'class_number': 55},
          {'value': false, 'class_name': 'Reach Stacker', 'class_number': 56},
          {'value': false, 'class_name': 'Straddle Carrier', 'class_number': 57},
          {'value': false, 'class_name': 'Mobile Crane', 'class_number': 59},
          {'value': false, 'class_name': 'Haul Truck', 'class_number': 61},
          {'value': false, 'class_name': 'Tractor', 'class_number': 62},
          {'value': false, 'class_name': 'Cement Mixer', 'class_number': 65},
          {'value': false, 'class_name': 'Ground Grader', 'class_number': 66},
          {'value': false, 'class_name': 'Aircraft Hangar', 'class_number': 74},
          {'value': false, 'class_name': 'Helipad', 'class_number': 84},
          {'value': false, 'class_name': 'Pylon', 'class_number': 93},
          {'value': false, 'class_name': 'Tower Crane', 'class_number': 94}
        ],
        // headersCommon: [
        //   {'text': 'class_name', 'align': 'left', 'sortable': false, 'value': 'class_name'},
        //   {'text': 'Class ID', 'align': 'left', 'sortable': false, 'value': 'class_number'}
        // ],
        itemsCommon: [
          {'value': false, 'class_name': 'Passenger/Cargo Plane', 'class_number': 13},
          {'value': false, 'class_name': 'Passenger Vehicle', 'class_number': 17},
          {'value': false, 'class_name': 'Small Car', 'class_number': 18},
          {'value': false, 'class_name': 'Bus', 'class_number': 19},
          {'value': false, 'class_name': 'Pickup Truck', 'class_number': 20},
          {'value': false, 'class_name': 'Utility Truck', 'class_number': 21},
          {'value': false, 'class_name': 'Truck', 'class_number': 23},
          {'value': false, 'class_name': 'Cargo Truck', 'class_number': 24},
          {'value': false, 'class_name': 'Truck Tractor w/ Box Trailer', 'class_number': 25},
          {'value': false, 'class_name': 'Truck Tractor', 'class_number': 26},
          {'value': false, 'class_name': 'Trailer', 'class_number': 27},
          {'value': false, 'class_name': 'Truck Tractor w/ Flatbed Trailer', 'class_number': 28},
          {'value': false, 'class_name': 'Passenger Car', 'class_number': 34},
          {'value': false, 'class_name': 'Cargo/Container Car', 'class_number': 35},
          {'value': false, 'class_name': 'Motorboat', 'class_number': 41},
          {'value': false, 'class_name': 'Fishing Vessel', 'class_number': 47},
          {'value': false, 'class_name': 'Dump Truck', 'class_number': 60},
          {'value': false, 'class_name': 'Front Loader/Bulldozer', 'class_number': 63},
          {'value': false, 'class_name': 'Excavator', 'class_number': 64},
          {'value': false, 'class_name': 'Hut/Tent', 'class_number': 71},
          {'value': false, 'class_name': 'Shed', 'class_number': 72},
          {'value': false, 'class_name': 'Building', 'class_number': 73},
          {'value': false, 'class_name': 'Damaged/Demolished Building', 'class_number': 76},
          {'value': false, 'class_name': 'Facility', 'class_number': 77},
          {'value': false, 'class_name': 'Construction Site', 'class_number': 79},
          {'value': false, 'class_name': 'Vehicle Lot', 'class_number': 83},
          {'value': false, 'class_name': 'Storage tank', 'class_number': 86},
          {'value': false, 'class_name': 'Shipping Container Lot', 'class_number': 89},
          {'value': false, 'class_name': 'Shipping Container', 'class_number': 91}
        ]
      }
    },
    mounted () {
      // console.log('Challenge Evaluation component mounted')
    }
  }
</script>

<style>
  .data-table {
    max-width: 600px;
    /*padding-left: 12rem;*/
    /*padding-right: 12rem; */
  }
</style>



// WEBPACK FOOTER //
// src/components/Challenge/ChallengeEvaluation.vue